/**
 * Orders (ARCHITECTURE.md §8, §4).
 *
 * The order blotter: every submitted order — dry-run and live — with its Toss
 * lifecycle status, fills, size/price, a link to the decision that produced it
 * (the "why" audit, §0.3), and an operator cancel for still-open orders. Dry-run
 * rows are tagged so simulated orders are never mistaken for live ones (§0.5).
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Badge, cn, formatDateTime, formatDecimal, formatMoney } from "@richrich/ui";
import type { Order, OrderStatus } from "@richrich/contracts";
import { useI18n } from "@richrich/i18n/react";
import type { MessageKey } from "@richrich/i18n";
import { PageShell } from "../components/PageShell";
import { Button, EmptyState, ErrorNote } from "../components/ui/controls";
import {
  isCancelable,
  orderStatusBadge,
  ordersInfiniteQuery,
  type OrderFilter,
} from "../features/orders/api";
import { useCancelOrder } from "../features/orders/hooks";

/** i18n key for a lifecycle status, or `null` to fall back to the raw token. */
function orderStatusKey(status: OrderStatus): MessageKey | null {
  switch (status) {
    case "FILLED":
      return "orders.statusFilled";
    case "PARTIAL_FILLED":
      return "orders.statusPartial";
    case "PENDING":
      return "orders.statusPending";
    case "intent":
      return "orders.statusIntent";
    case "PENDING_CANCEL":
      return "orders.statusCancelling";
    case "PENDING_REPLACE":
      return "orders.statusReplacing";
    case "CANCELED":
      return "orders.statusCanceled";
    case "REPLACED":
      return "orders.statusReplaced";
    case "REJECTED":
      return "orders.statusRejected";
    case "CANCEL_REJECTED":
      return "orders.statusCancelRejected";
    case "REPLACE_REJECTED":
      return "orders.statusReplaceRejected";
    default:
      return null;
  }
}

export const Route = createFileRoute("/orders")({
  loader: async ({ context }) => {
    await context.queryClient.ensureInfiniteQueryData(ordersInfiniteQuery("all"));
  },
  component: OrdersPage,
});

const FILTERS: readonly { value: OrderFilter; labelKey: MessageKey }[] = [
  { value: "all", labelKey: "orders.filterAll" },
  { value: "OPEN", labelKey: "orders.filterOpen" },
  { value: "CLOSED", labelKey: "orders.filterClosed" },
];

function OrdersPage() {
  const { t } = useI18n();
  const [filter, setFilter] = useState<OrderFilter>("all");
  const query = useInfiniteQuery(ordersInfiniteQuery(filter));
  const orders = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <PageShell
      eyebrow={t("orders.eyebrow")}
      title={t("orders.title")}
      description={t("orders.description")}
      loading={query.isLoading}
      actions={
        <div className="flex items-center gap-1 rounded-md border border-hairline bg-surface p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                filter === f.value ? "bg-elevated text-ink" : "text-muted hover:text-ink",
              )}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      }
    >
      {query.isError ? (
        <ErrorNote>{t("orders.loadError")}</ErrorNote>
      ) : orders.length === 0 ? (
        <EmptyState title={t("orders.emptyTitle")} description={t("orders.emptyDescription")} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto rounded-panel border border-hairline bg-surface">
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3 font-medium">{t("orders.colSymbol")}</th>
                  <th className="px-4 py-3 font-medium">{t("orders.colSide")}</th>
                  <th className="px-4 py-3 font-medium">{t("orders.colType")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("orders.colSize")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("orders.colPrice")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("orders.colFilled")}</th>
                  <th className="px-4 py-3 font-medium">{t("orders.colStatus")}</th>
                  <th className="px-4 py-3 font-medium">{t("orders.colPlaced")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("orders.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <OrderRow key={order.id} order={order} />
                ))}
              </tbody>
            </table>
          </div>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                pending={query.isFetchingNextPage}
                onClick={() => query.fetchNextPage()}
              >
                {t("orders.loadMore")}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </PageShell>
  );
}

function fracLen(value: string): number {
  const dot = value.indexOf(".");
  return dot === -1 ? 0 : value.length - dot - 1;
}

function sizeText(order: Order): string {
  if (order.qty !== null) {
    return formatDecimal(order.qty, { fractionDigits: fracLen(order.qty) });
  }
  if (order.orderAmount !== null) {
    return formatMoney(order.orderAmount, order.currency);
  }
  return "—";
}

function OrderRow({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const cancel = useCancelOrder();
  const badge = orderStatusBadge(order.status);
  const statusKey = orderStatusKey(order.status);
  const typeLabel =
    order.orderType === "MARKET"
      ? t("orders.typeMarket")
      : order.orderType === "LIMIT"
        ? t("orders.typeLimit")
        : order.orderType;
  const filled =
    order.qty !== null
      ? `${formatDecimal(order.filledQty, { fractionDigits: fracLen(order.filledQty) })} / ${formatDecimal(order.qty, { fractionDigits: fracLen(order.qty) })}`
      : formatDecimal(order.filledQty, { fractionDigits: fracLen(order.filledQty) });

  return (
    <tr className="border-b border-hairline/60 last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-mono font-medium text-ink">{order.symbol}</span>
          {order.marketCountry !== null ? (
            <span className="font-mono text-[10px] uppercase text-muted">
              {order.marketCountry}
            </span>
          ) : null}
          {order.dryRun ? (
            <Badge tone="neutral">{t("orders.badgeDryRun")}</Badge>
          ) : (
            <Badge tone="warning">{t("orders.badgeLive")}</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "font-mono text-xs font-semibold",
            order.side === "BUY" ? "text-good-text" : "text-critical",
          )}
        >
          {order.side === "BUY" ? t("orders.sideBuy") : t("orders.sideSell")}
        </span>
      </td>
      <td className="px-4 py-3 text-ink-secondary">
        {typeLabel}
        {order.timeInForce !== null ? (
          <span className="ml-1 text-xs text-muted">{order.timeInForce}</span>
        ) : null}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink">{sizeText(order)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink">
        {order.orderType === "MARKET"
          ? t("orders.priceMarket")
          : order.price !== null
            ? formatMoney(order.price, order.currency)
            : "—"}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-secondary">
        {filled}
        {order.avgFillPrice !== null ? (
          <div className="text-xs text-muted">
            @ {formatMoney(order.avgFillPrice, order.currency)}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <Badge tone={badge.tone} dot>
          {statusKey !== null ? t(statusKey) : badge.label}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-muted">
        {order.submittedAt !== null
          ? formatDateTime(order.submittedAt, locale)
          : formatDateTime(order.createdAt, locale)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          {order.strategyId !== null && order.runId !== null ? (
            <Link
              to="/strategies/$id/runs/$runId"
              params={{ id: order.strategyId, runId: order.runId }}
              className="text-xs font-medium text-brand hover:underline"
            >
              {t("orders.decision")}
            </Link>
          ) : null}
          {isCancelable(order) ? (
            <Button
              variant="danger"
              size="sm"
              pending={cancel.isPending}
              onClick={() => cancel.mutate(order.id)}
            >
              {t("common.cancel")}
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
